import { useState } from 'react';
import { ChevronDown, ChevronRight, Search, ChefHat, Users } from 'lucide-react';
import clsx from 'clsx';
import type { ScheduleEmployee } from '../../hooks/useScheduleBuilder';
import type { ShiftInstance } from '../../services/api';
import EmployeeCard from './EmployeeCard';

// BOH = Back of House (Kitchen)
const BOH_ROLES = new Set(['cook', 'dishwasher']);
// FOH = Front of House (Floor)
const FOH_ROLES = new Set(['server', 'bartender', 'host', 'busser', 'manager']);

interface ScheduleBuilderSidebarProps {
  employeesByRole: Record<string, ScheduleEmployee[]>;
  assignedEmployeeIds: Set<string>;
  isEmployeeAvailable: (employee: ScheduleEmployee, shift: ShiftInstance) => boolean;
  selectedShift?: ShiftInstance | null;
}

export default function ScheduleBuilderSidebar({
  employeesByRole,
  assignedEmployeeIds,
  isEmployeeAvailable,
  selectedShift,
}: ScheduleBuilderSidebarProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showBOH, setShowBOH] = useState(true);
  const [showFOH, setShowFOH] = useState(true);

  // Flatten all employees and group by BOH/FOH
  const allEmployees = Object.values(employeesByRole).flat();

  const bohEmployees = allEmployees.filter(emp => BOH_ROLES.has(emp.primaryRole));
  const fohEmployees = allEmployees.filter(emp => FOH_ROLES.has(emp.primaryRole));

  // Filter by search
  const filterBySearch = (employees: ScheduleEmployee[]) => {
    if (!searchQuery) return employees;
    const query = searchQuery.toLowerCase();
    return employees.filter(
      emp =>
        emp.firstName.toLowerCase().includes(query) ||
        emp.lastName.toLowerCase().includes(query)
    );
  };

  const filteredBOH = filterBySearch(bohEmployees);
  const filteredFOH = filterBySearch(fohEmployees);

  // Count available
  const countAvailable = (employees: ScheduleEmployee[]) => {
    return employees.filter(
      emp =>
        !assignedEmployeeIds.has(emp.id) &&
        (!selectedShift || isEmployeeAvailable(emp, selectedShift))
    ).length;
  };

  return (
    <div className="w-56 flex-shrink-0 bg-white rounded-lg border border-slate-200 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search staff..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
          />
        </div>
      </div>

      {/* Staff list */}
      <div className="flex-1 overflow-y-auto">
        {/* Kitchen / BOH */}
        {filteredBOH.length > 0 && (
          <div className="border-b border-slate-100">
            <button
              onClick={() => setShowBOH(!showBOH)}
              className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                {showBOH ? (
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                )}
                <ChefHat className="w-4 h-4 text-orange-500" />
                <span className="font-medium text-sm text-slate-700">Kitchen</span>
              </div>
              <span className="text-xs text-slate-500">
                {countAvailable(filteredBOH)}/{filteredBOH.length}
              </span>
            </button>

            {showBOH && (
              <div className="px-2 pb-2 space-y-1">
                {filteredBOH.map((employee) => {
                  const isAssigned = assignedEmployeeIds.has(employee.id);
                  const isAvailable = selectedShift
                    ? isEmployeeAvailable(employee, selectedShift)
                    : true;

                  return (
                    <EmployeeCard
                      key={employee.id}
                      employee={employee}
                      isAvailable={isAvailable}
                      isAssigned={isAssigned}
                      compact
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Floor / FOH */}
        {filteredFOH.length > 0 && (
          <div>
            <button
              onClick={() => setShowFOH(!showFOH)}
              className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                {showFOH ? (
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                )}
                <Users className="w-4 h-4 text-blue-500" />
                <span className="font-medium text-sm text-slate-700">Floor</span>
              </div>
              <span className="text-xs text-slate-500">
                {countAvailable(filteredFOH)}/{filteredFOH.length}
              </span>
            </button>

            {showFOH && (
              <div className="px-2 pb-2 space-y-1">
                {filteredFOH.map((employee) => {
                  const isAssigned = assignedEmployeeIds.has(employee.id);
                  const isAvailable = selectedShift
                    ? isEmployeeAvailable(employee, selectedShift)
                    : true;

                  return (
                    <EmployeeCard
                      key={employee.id}
                      employee={employee}
                      isAvailable={isAvailable}
                      isAssigned={isAssigned}
                      compact
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}

        {filteredBOH.length === 0 && filteredFOH.length === 0 && (
          <div className="text-center py-8 text-slate-400">
            <Users className="w-6 h-6 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No staff found</p>
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="px-3 py-2 border-t border-slate-100 bg-slate-50">
        <p className="text-[10px] text-slate-400 text-center">
          Drag to shifts or use + Add Staff
        </p>
      </div>
    </div>
  );
}
